import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

import MintOptions from '../components/MintOptions';
import MintAnotherComponent from '../components/MintAnotherComponent';

import { showErrorMessage, showWarningMessage } from '../utils/UIUtils';
import { handleError, isUserRejection } from '../utils/ErrorHandler';
import * as Errors from '../utils/ErrorMessages';

import { getContractWithSigner, fetchCollectionStatus, isOnCorrectNetwork, preflightMint } from '../utils/BlockchainAPI';

import generateDynaStripes from '../utils/DynaStripes';
import DynaSpan from '../components/DynaSpan';
import getTextTraits from '../utils/Traits';
import { DynaStripesMaxTokensPerUser, DynaStripesCurrentNetworkCurrencySymbol, DynaStripesCurrentNetworkName } from '../utils/Constants';
import { hueToRgb } from '../utils/Colour';

// The contract rejects a tint of 230/255 or more, and Math.round(0.9 * 255) is
// exactly 230 -- so the usable ceiling is just under 90%.
const MAX_TINT_ALPHA = 0.89;

const MintPage = () => {
  const [doneSuccess, setDoneSuccess] = useState(false);
  const [randomSeed, setRandomSeed] = useState(() => Math.trunc(Math.random() * 5_000_000));
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const [rotationRange, setRotationRange] = useState(0);
  const [zoom, setZoom] = useState(50);
  const [tintColour, setTintColour] = useState({ r: 200, g: 200, b: 200, a: 0.2 });
  const [widthRange, setWidthRange] = useState([25, 250]);
  const [speedRange, setSpeedRange] = useState([25, 250]);
  const [mintPrice, setMintPrice] = useState<bigint | null>(null); // wei value as BigInt
  const [formattedMintPrice, setFormattedMintPrice] = useState<string | null>(null); // formatted string for display
  const [tokenCount, setTokenCount] = useState<string | null>(null);
  const [tokenLimit, setTokenLimit] = useState<string | null>(null);
  // Minting can't be offered until we know the price and supply.
  const [isCollectionLoading, setIsCollectionLoading] = useState(true);
  const [txHash, setTxHash] = useState<string | null>(null);

  const randomIntFromInterval = (min: number, max: number) => {
    if (max <= min) {
      return min;
    }
    min = Math.ceil(min);
    max = Math.floor(max);
    const randomVal = Math.floor(Math.random() * (max - min + 1)) + min;
    return Math.round(randomVal);
  }

  const generateRandomParams = useCallback(() => {
    const randomZoom = randomIntFromInterval(0, 100);

    const randomRed = randomIntFromInterval(0, 255);
    const randomBlue = randomIntFromInterval(0, 255);
    const randomGreen = randomIntFromInterval(0, 255);
    const randomAlpha = Math.random() * MAX_TINT_ALPHA;
    const randomColour = { r: randomRed, g: randomGreen, b: randomBlue, a: randomAlpha };

    const randomRotationDegrees = randomIntFromInterval(0, 360);
    const randomRotationRange = randomIntFromInterval(0, 180);

    const randomWidthA = randomIntFromInterval(25, 250);
    const randomWidthB = randomIntFromInterval(25, 250);
    const randomWidthMin = Math.min(randomWidthA, randomWidthB);
    const randomWidthMax = Math.max(randomWidthA, randomWidthB);

    const randomSpeedA = randomIntFromInterval(25, 250);
    const randomSpeedB = randomIntFromInterval(25, 250);
    const randomSpeedMin = Math.min(randomSpeedA, randomSpeedB);
    const randomSpeedMax = Math.max(randomSpeedA, randomSpeedB);

    setZoom(randomZoom);
    setTintColour(randomColour);
    setRotationDegrees(randomRotationDegrees);
    setRotationRange(randomRotationRange);
    setWidthRange([randomWidthMin, randomWidthMax]);
    setSpeedRange([randomSpeedMin, randomSpeedMax]);
  }, []);

  useEffect(() => {
    console.log('[MintPage] useEffect');

    const loadCollectionStatus = async () => {
      setIsCollectionLoading(true);
      try {
        const status = await fetchCollectionStatus();
        setMintPrice(status.mintPrice);
        setFormattedMintPrice(status.formattedMintPrice);
        setTokenCount(status.tokenCount);
        setTokenLimit(status.tokenLimit);
      } catch (err) {
        console.error('[MintPage] Could not fetch collection status', err);
        showErrorMessage("Couldn't reach the network to fetch the mint price. Please try again shortly.");
        setMintPrice(null);
        setFormattedMintPrice(null);
        setTokenCount(null);
        setTokenLimit(null);
      } finally {
        setIsCollectionLoading(false);
      }
    }

    loadCollectionStatus();
    generateRandomParams();
  }, [generateRandomParams]);

  const generateRandomSeed = () => {
    setRandomSeed(Math.trunc(Math.random() * 5_000_000));
  }

  const zoomChanged = (value: any, index: any) => {
    console.log("Zoom changed to: " + value);
    setZoom(value);
  }

  const tintColourChanged = (hue: number) => {
    // Preserve alpha, take a fully saturated colour for the chosen hue.
    setTintColour(prev => ({ ...prev, ...hueToRgb(hue) }));
  }

  const tintAlphaChanged = (alphaPercent: number) => {
    let newAlpha = alphaPercent / 100;

    if (newAlpha > MAX_TINT_ALPHA) {
      newAlpha = MAX_TINT_ALPHA;
      showWarningMessage("Max tint is " + Math.round(MAX_TINT_ALPHA * 100) + "%. ");
    }
    // Update alpha and preserve colour
    setTintColour(prev => ({
      ...prev,
      a: newAlpha
    }));
  }


  const rotationDegreesChanged = (value: any, index: any) => {
    console.log("Rotation range changed to: " + value);
    setRotationDegrees(value);
  }
  const rotationRangeChanged = (value: any, index: any) => {
    console.log("Rotation variation changed to: " + value);
    setRotationRange(value);
  }

  const widthRangeChanged = (value: any, index: any) => {
    setWidthRange(value);
  }

  const speedRangeChanged = (value: any, index: any) => {
    setSpeedRange(value);
  }

  const mintAnother = () => {
    setDoneSuccess(false);
    generateRandomSeed(); // This sets random seed state
    generateRandomParams();
  }

  const mint = async () => {
    const tintRed = tintColour.r;
    const tintGreen = tintColour.g;
    const tintBlue = tintColour.b;
    const tintAlpha = Math.round(tintColour.a * 255);
    const widthMin = widthRange[0];
    const widthMax = widthRange[1];
    const speedMin = speedRange[0];
    const speedMax = speedRange[1];

    if (randomSeed < 0 || randomSeed >= 5_000_000) {
      showErrorMessage("Random seed invalid.");
      return;
    }
    if (zoom < 0 || zoom > 100) {
      showErrorMessage("Zoom must be between 0 and 100.");
      return;
    }
    if (tintRed < 0 || tintRed > 255 || tintGreen < 0 || tintGreen > 255 || tintBlue < 0 || tintBlue > 255) {
      showErrorMessage("Tint color not valid");
      return;
    }
    if (tintAlpha >= 230) {
      showErrorMessage("Tint percentage is too high.");
      return;
    }
    if (rotationDegrees < 0 || rotationDegrees > 360) {
      showErrorMessage("Rotation degrees must be between 0 and 360.");
      return;
    }
    if (rotationRange < 0 || rotationRange > 360) {
      showErrorMessage("Rotation range must be between 0 and 360.");
      return;
    }
    if (widthMin < 25 || widthMin > widthMax || widthMax > 250) {
      showErrorMessage("Stripe width must be between 25 and 250.")
      return;
    }
    if (speedMin < 25 || speedMin > speedMax || speedMax > 250) {
      showErrorMessage("Speed must be between 25 and 250.")
      return;
    }

    if (mintPrice === undefined || mintPrice === null) {
      toast.error("Couldn't fetch mint price. Try refreshing this page.");
      return;
    }

    // Tracks whether we proved the mint valid, so a wallet failure afterwards
    // can be reported accurately.
    let dryRunPassed = false;

    try {
      const correctNetwork = await isOnCorrectNetwork();
      if (!correctNetwork) {
        console.log("Not on right network");
        throw Error(Errors.DS_WRONG_ETH_NETWORK);
      }
      const contractWithSigner = await getContractWithSigner();

      // Get account from signer instead of window.ethereum to avoid Coinbase Wallet bug
      const signer = await contractWithSigner.runner as any;
      const account = await signer.getAddress();
      const tokenCount = await contractWithSigner.balanceOf(account);
      console.log("User's token count: " + tokenCount.toString());
      if (tokenCount >= DynaStripesMaxTokensPerUser && account !== "0xBC10a3aE909B1b94f4C3E39607aD19D386dCe32a") {
        showErrorMessage("You already have " + tokenCount + " DynaStripes artworks. You've reached the mint limit of " + DynaStripesMaxTokensPerUser + " tokens!");
        return;
      }

      console.log("Minting dynastripes: " + rotationDegrees + " " + zoom + " " + widthMin + " " + widthMax + " " + speedMin + " " + speedMax)

      const mintArgs = [randomSeed, zoom, tintRed, tintGreen, tintBlue, tintAlpha, rotationDegrees, rotationRange, widthMin, widthMax, speedMin, speedMax];

      // Check the mint against the chain ourselves before involving the wallet.
      const preflight = await preflightMint(account, mintArgs, mintPrice);
      if (preflight.reason === 'insufficient-funds') {
        showErrorMessage(
          `You need about ${preflight.required} ${DynaStripesCurrentNetworkCurrencySymbol} to cover the gas fee, but this account holds ${preflight.balance}. `
          + `Top up with ${DynaStripesCurrentNetworkCurrencySymbol} on ${DynaStripesCurrentNetworkName} -- see the 'How to' guide.`);
        return;
      }
      if (preflight.reason === 'revert') {
        showErrorMessage("This mint wouldn't succeed: " + preflight.detail + ". Try adjusting your settings.");
        return;
      }
      dryRunPassed = true;

      // No hardcoded gasLimit: ethers and the wallet estimate it. The old
      // fixed 270000 happened to still fit (a mint measures ~192k-210k) but
      // it would silently become an out-of-gas revert -- paid for by the user
      // -- if costs ever shifted.
      const overrides = {
        value: mintPrice
      }

      const transaction = await contractWithSigner.mintStripes(...mintArgs, overrides);
      console.log("Tx hash: " + transaction.hash);

      setDoneSuccess(true);
      setTxHash(transaction.hash);

      toast.success("Successfully minted your DynaStripes NFT!");
    } catch (err: any) {
      if (err.code === "UNSUPPORTED_OPERATION" && err.message?.startsWith("unknown account")) {
        showErrorMessage("You need to connect an account to your wallet. Read the 'How to' guide for more info.");
      } else if (dryRunPassed && !isUserRejection(err) && err?.code !== 'INSUFFICIENT_FUNDS') {
        // Only for errors we genuinely can't account for. Anything ethers
        // names -- a rejection, insufficient funds -- is handled properly
        // below rather than blamed on the wallet.
        // We already proved this mint valid on-chain, so the wallet is the
        // problem -- Phantom, for one, can't simulate this contract and
        // returns a bare -32603. Say so rather than blaming the mint.
        console.error('[MintPage] Wallet rejected a mint we verified as valid', err);
        showErrorMessage("Your wallet wouldn't send the transaction, but we checked it against Polygon and it is valid. If your wallet warns it can't simulate the request you can safely confirm, or try a different wallet.");
      } else {
        handleError(err);
      }
    }
  }

  console.log('[MintPage] render');

  const traits = getTextTraits(zoom, tintColour.r, tintColour.g, tintColour.b, Math.round(tintColour.a * 255), rotationDegrees, rotationRange, widthRange[0], widthRange[1], speedRange[0], speedRange[1]);

  const svg = generateDynaStripes(randomSeed, zoom, tintColour, rotationDegrees, rotationRange, widthRange[0], widthRange[1], speedRange[0], speedRange[1]);
  const encodedSvg = encodeURIComponent(svg);
  const svgDataUri = `data:image/svg+xml,${encodedSvg}`;

  return (
    <div className="mainContent">
      <div id="mint" className="content">
        <h1>Mint your own <DynaSpan /> NFT</h1>
        <center>
          {doneSuccess ? <MintAnotherComponent txHash={txHash} mintAnother={mintAnother} svgDataUri={svgDataUri} /> : <MintOptions svg={svg} traits={traits} mintPrice={mintPrice} formattedMintPrice={formattedMintPrice} tokenCount={tokenCount} tokenLimit={tokenLimit} isCollectionLoading={isCollectionLoading} rotationDegrees={rotationDegrees} rotationDegreesChanged={rotationDegreesChanged} rotationRange={rotationRange} rotationRangeChanged={rotationRangeChanged} zoom={zoom} zoomChanged={zoomChanged} tintColour={tintColour} tintColourChanged={tintColourChanged} tintAlphaChanged={tintAlphaChanged} widthRange={widthRange} widthRangeChanged={widthRangeChanged} speedRange={speedRange} speedRangeChanged={speedRangeChanged} mint={mint} refreshSeed={generateRandomSeed} refreshParams={generateRandomParams} />}
        </center>
      </div>
    </div>
  );
}


export default MintPage;